import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormHelperText,
  Checkbox,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import { BglPrimaryButton, BglPrimaryOutlinedButton, BglSecondaryButton } from 'components/button/BglButtons';
import { BglOverlayLoader } from 'components/loading';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileSignatureIcon from '@mui/icons-material/EditNote';
import InfoOutlinedIcon from '@mui/icons-material/Info';
import { ApiErrorInfo } from 'types';
import { PilotApis } from 'apis/pilotAPI';
import { useToast } from '../../context/ToastContext';
import {
  createProjectPartTwoSchema,
  type CreateProjectPartTwoValues,
} from 'types/validators/project-validators';
import { FORM_PART_ONE_KEY, FORM_PART_TWO_KEY, EDIT_PROJECT_DATA_KEY, ENVIRONMENT_CREATION_DATA_KEY, FORM_PART_ONE_CONFIGS_KEY } from './UtilityCreationPage';
import { DynamicConfigForm } from './DynamicConfigForm';
import { ConfigRegistryType, SaveProjectConfigData } from 'types/apis/pilotInterfaces';
import { useSelector, useDispatch } from 'react-redux';
import { selectConfigRegistryData, clearConfigRegistryData, setConfigRegistryData } from 'store/dataReducers/configRegistrySlice';
import { selectAdditionalConfigData, setAdditionalConfigData, clearFormData } from 'store/dataReducers/formDataSlice';
import { FIRST_STEP_CONFIG_MAPPING } from './configMapping';
import { ErrorHandler } from 'apis/pilotAPI';
import { filterConfigsNoDuplicates, isDuplicateConfig, getConfigsToSave, processConfigValue, filterRegistryByComponents } from './registryUtils';
import { DatabaseZap } from 'lucide-react';
import { Package } from 'lucide-react';
import { Send } from 'lucide-react';


// Removed measurementTypeIcons as it's no longer needed with dynamic form

export const UtilityDetailsPage = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiErrorInfo>();
  const [stepOneData, setStepOneData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingRegistry, setIsLoadingRegistry] = useState(false);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [configGroupSelection, setConfigGroupSelection] = useState<{[key: string]: string}>({});
  const [stackNameError, setStackNameError] = useState<string | null>(null);
  const dispatch = useDispatch();
  
  // Regex pattern for stack name validation: ^[a-z0-9]+$
  const STACK_NAME_REGEX = /^[a-z0-9]+$/;
  const configRegistryData = useSelector(selectConfigRegistryData);
  const additionalConfigData = useSelector(selectAdditionalConfigData);
  const scrollContainerRef = useRef<HTMLDivElement>(null);


  const { control, handleSubmit, watch, setValue, getValues, trigger, formState: { errors } } = useForm<CreateProjectPartTwoValues>({
    resolver: zodResolver(createProjectPartTwoSchema),
    defaultValues: {},
  });

  // Watch values for conditional rendering
  const stackNameValue = watch("stackName") as string | undefined;

  // Function to scroll to top
  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle config group selection changes
  const handleConfigGroupSelectionChange = (groupSelection: {[key: string]: string}) => {
    console.log('handleConfigGroupSelectionChange', groupSelection);
    setConfigGroupSelection(groupSelection);
  };

  // Function to manually reload registry data
  const reloadRegistryData = async () => {
    if (isLoadingRegistry) return;
    
    setIsLoadingRegistry(true);
    setRegistryError(null);
    try {
      const response = await PilotApis.getConfigRegistry(rc);
                if (response.success && response.data) {
            dispatch(setConfigRegistryData(response.data));
            console.log('Registry data reloaded successfully:', response.data);
            toast.success('Configuration registry loaded successfully');
          } else {
        const errorMsg = response.error || 'Failed to load configuration registry';
        console.error('Failed to reload registry data:', errorMsg);
        setRegistryError(errorMsg);
        toast.error('Failed to load configuration registry. Please try again.');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error reloading registry data:', errorMsg);
      setRegistryError(errorMsg);
      toast.error('Failed to load configuration registry. Please try again.');
    } finally {
      setIsLoadingRegistry(false);
    }
  };

  // Effect to scroll to top when error occurs
  useEffect(() => {
    if (error) {
      scrollToTop();
    }
  }, [error]);

  // Effect to reload registry data if it's empty (e.g., after page refresh)
  useEffect(() => {
    const loadRegistryData = async () => {
      if (configRegistryData.length === 0 && !isLoadingRegistry) {
        setIsLoadingRegistry(true);
        setRegistryError(null);
        try {
          const response = await PilotApis.getConfigRegistry(rc);
          if (response.success && response.data) {
            dispatch(setConfigRegistryData(response.data));
            console.log('Registry data reloaded successfully:', response.data);
          } else {
            const errorMsg = response.error || 'Failed to load configuration registry';
            console.error('Failed to reload registry data:', errorMsg);
            setRegistryError(errorMsg);
            toast.error('Failed to load configuration registry. Please try again.');
          }
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
          console.error('Error reloading registry data:', errorMsg);
          setRegistryError(errorMsg);
          toast.error('Failed to load configuration registry. Please try again.');
        } finally {
          setIsLoadingRegistry(false);
        }
      }
    };

    loadRegistryData();
  }, [configRegistryData.length, dispatch, rc, isLoadingRegistry]);

  // Effect to log registry data changes
  useEffect(() => {
    if (configRegistryData.length > 0) {
      console.log('Registry data updated, count:', configRegistryData.length);
    }
  }, [configRegistryData]);



  // Helper function to filter out first step configs
  const filterFirstStepConfigs = (configData: ConfigRegistryType[]): ConfigRegistryType[] => {
    // const firstStepConfigKeys = FIRST_STEP_CONFIG_MAPPING.map(m => m.configKey);
    
    const { filterConfigsNoDuplicates } = require('./registryUtils');
    return filterConfigsNoDuplicates(
      configData,
      (config: any) => config.utilityConfigurationRequired && (config.configClassification === 'SYSTEM' || config.configClassification === 'FEATURE')
    );
  };

  // Helper function to apply FIRST_STEP_CONFIG_MAPPING to config registry data
  const applyFirstStepConfigMapping = (configData: ConfigRegistryType[], stepOneData: any) => {
    if (!configData.length || !stepOneData) return configData;
    
    console.log('Applying FIRST_STEP_CONFIG_MAPPING from step 1 data');
    
    // Create a deep copy of configData to avoid modifying immutable objects
    const updatedConfigData = configData.map(configType => ({
      ...configType,
      configs: configType.configs.map(config => {
        const mapping = FIRST_STEP_CONFIG_MAPPING.find(m => m.configKey === config.configKey);
        if (mapping) {
          const stepOneValue = stepOneData[mapping.fieldName];
          if (stepOneValue !== undefined && stepOneValue !== null) {
            console.log(`Mapping ${mapping.fieldName} (${stepOneValue}) to config ${mapping.configKey}`);
            return {
              ...config,
              config_value: processConfigValue(stepOneValue, config.configValueMetadata)
            };
          }
        }
        return config;
      })
    }));
    
    return updatedConfigData;
  };

  // State to store the updated config registry data with step 1 values applied
  const [updatedConfigRegistryData, setUpdatedConfigRegistryData] = useState<ConfigRegistryType[]>([]);

  // Effect to set default values for config fields when config registry data is available
  useEffect(() => {
    if (configRegistryData.length > 0) {
      // Set default values for config fields that are required for utility configuration and not FILE classification
      // Exclude first step configs from being set as defaults in second step
      const filteredConfigData = filterRegistryByComponents(configRegistryData)
      //  filterFirstStepConfigs(configRegistryData);
      
      filteredConfigData.forEach((configType: ConfigRegistryType) => {
        configType.configs.forEach((config: any) => {

          if (config.utilityConfigurationRequired) {
            const fieldName = `${configType.configType}.${config.configKey}`;
            let defaultValue: any = '';
            
            if (config.configDataType === 'BOOLEAN') {
              defaultValue = config.defaultValue === 'true' || false;
            } else {
              defaultValue = config.defaultValue || '';
            }
            
            setValue(fieldName, defaultValue);
          }
        });
      });
      
      // If step 1 data is already loaded, apply FIRST_STEP_CONFIG_MAPPING to update config values
      if (stepOneData) {
        const updatedData = applyFirstStepConfigMapping(filterRegistryByComponents(configRegistryData), stepOneData);
        // Store the updated config data locally instead of dispatching to Redux
        setUpdatedConfigRegistryData(updatedData);
      }
    }
  }, [configRegistryData, setValue, stepOneData]);

  // Additional config data is now loaded automatically by DynamicConfigForm
  // No need to manually load here as DynamicConfigForm handles loading from Redux

  // Effect to set default stackName when stepOneData is loaded
  useEffect(() => {
    if (stepOneData) {
      // Check if existing environment was selected - use its stack_name
      if (stepOneData.environment_id) {
        // Using existing environment - use its stack name (from step 1)
        setValue("stackName", stepOneData.stackName || '');
      } else if (stepOneData.utility?.name) {
        // For new environment, generate default from utility name
        // Regex pattern: ^[a-z0-9]+$
        const currentStackName = getValues("stackName");
        if (!currentStackName) {
          const defaultStackName = stepOneData.utility.name
            ?.toLowerCase()
            .replace(/[^a-z0-9]/g, '')  // Remove non-alphanumeric characters
            .substring(0, 50) || '';
          setValue("stackName", defaultStackName);
        }
      }
    }
  }, [stepOneData, setValue, getValues]);

  useEffect(() => {
    // This page is only for create mode step 2
    // Load step 1 data from sessionStorage for create mode
    const storedData = sessionStorage.getItem(FORM_PART_ONE_KEY);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setStepOneData(parsedData);
        
        // This page is always create mode step 2
        setIsEditMode(false);
        
        console.log("Detected mode: create (step 2)");
        
        // Prefill all step 2 data dynamically (for create mode)
        Object.keys(parsedData).forEach(key => {
          if (parsedData[key] !== undefined && parsedData[key] !== null) {
            console.log(`Setting ${key}:`, parsedData[key]);
            setValue(key, parsedData[key]);
          }
        });
        
        // Apply FIRST_STEP_CONFIG_MAPPING to update config values based on step 1 form data
        if (configRegistryData.length > 0) {
          const updatedData = applyFirstStepConfigMapping(filterRegistryByComponents(configRegistryData), parsedData);
          // Store the updated config data locally instead of dispatching to Redux
          setUpdatedConfigRegistryData(updatedData);
        }
        
        // Load step 2 data from sessionStorage if available
        const storedStepTwoData = sessionStorage.getItem(FORM_PART_TWO_KEY);
        if (storedStepTwoData) {
          try {
            const parsedStepTwoData = JSON.parse(storedStepTwoData);
            console.log("Loading step 2 data from sessionStorage:", parsedStepTwoData);
            
            // Prefill all step 2 data dynamically (for create mode)
            Object.keys(parsedStepTwoData).forEach(key => {
              if (parsedStepTwoData[key] !== undefined && parsedStepTwoData[key] !== null) {
                console.log(`Setting ${key}:`, parsedStepTwoData[key]);
                setValue(key, parsedStepTwoData[key]);
              }
            });
          } catch (err) {
            console.error('Error loading step 2 data:', err);
            const errorMessage = ErrorHandler.handleApiError(err, { context: 'Loading step 2 data' });
            sessionStorage.removeItem(FORM_PART_TWO_KEY);
          }
        }
      } catch (err) {
        console.log('err', err);
        const errorMessage = ErrorHandler.handleApiError(err, { context: 'Loading project data' });
        // If data is corrupted, redirect back to step 1
        rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CREATE);
      }
    } else {
      // If no data, redirect back to step 1
      rc.uiRouter.navigate(ROUTE_TREE.PROJECT.CREATE);
    }

  }, [rc.uiRouter]);

  // Validate project combination before saving
  const validateProjectCombination = async (values: CreateProjectPartTwoValues): Promise<{ isValid: boolean; errorMessage?: string }> => {
    const formValues = getValues() as any;
    
    const validationData = {
      environment: (() => {
        const toolApiUrl = process.env.REACT_APP_TOOL_API_URL;
        return toolApiUrl?.includes('deto-prod-api.bidgely.com') ? 'uat' : 'dev';
      })(),
      queue_suffix: values.stackName,
      utility_name: stepOneData?.utility?.name || '',
      pilot_id: stepOneData?.projectId || '',
      state_alpha_code: stepOneData?.stateCode || '',
      customer_type: formValues?.launchpad_ingestion_configs?.default_user_segment=="RESIDENTIAL" ? "RESI" : "SMB"
    };

    const validationResponse = await PilotApis.validateProject(rc, validationData);
    
    if (!validationResponse.success) {
      const errorMessage = validationResponse.error?.message || validationResponse.error || 'Validation failed';
      return { isValid: false, errorMessage };
    }

    if (!validationResponse.is_valid) {
      // Build detailed error message from conflicts
      let conflictMessage = validationResponse.message || 'Combination already exists';
      if (validationResponse.conflicts) {
        const conflictDetails: string[] = [];
        if (validationResponse.conflicts.queue_suffix_exists) {
          conflictDetails.push('Queue suffix already exists');
        }
        if (validationResponse.conflicts.utility_name_exists) {
          conflictDetails.push('Utility name already exists');
        }
        if (validationResponse.conflicts.pilot_exists_in_stack) {
          conflictDetails.push('Pilot already exists in stack');
        }
        if (validationResponse.conflicts.existing_stack) {
          conflictDetails.push(`Conflicts with existing stack: ${validationResponse.conflicts.existing_stack.utility_name} (${validationResponse.conflicts.existing_stack.environment})`);
        }
        if (conflictDetails.length > 0) {
          conflictMessage += ': ' + conflictDetails.join(', ');
        }
      }
      return { isValid: false, errorMessage: conflictMessage };
    }

    return { isValid: true };
  };

  const onSubmit = async (values: CreateProjectPartTwoValues) => {
    // Ensure registry data is loaded before submission
    if (configRegistryData.length === 0) {
      toast.error('Configuration registry is not loaded. Please wait or retry.');
      return;
    }

    // Validate dynamic form fields before proceeding
    const isValid = await trigger(undefined, { shouldFocus: true } as any);
    if (!isValid) {
      toast.error('Please fill all required fields.');
      return;
    }

    // Validate stack name format
    const stackNameVal = (values as any).stackName || '';
    if (stackNameVal && !STACK_NAME_REGEX.test(stackNameVal)) {
      setStackNameError('Invalid format. Only lowercase letters and numbers allowed (e.g., utilityname).');
      toast.error('Please fix the Stack Name format.');
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      // Validate project combination before proceeding
      const validationResult = await validateProjectCombination(values);
      if (!validationResult.isValid) {
        setError({ message: validationResult.errorMessage || 'Validation failed', code: 'VALIDATION_FAILED' });
        toast.error(validationResult.errorMessage || 'Validation failed');
        setIsLoading(false);
        return;
      }
      // Additional config data is saved to Redux by DynamicConfigForm; also persist step 2 values
      try {
        sessionStorage.setItem(FORM_PART_TWO_KEY, JSON.stringify(values));
      } catch (e) {
        console.error('Failed to save step 2 data to session storage:', e);
      }

      // Step 2 flow:
      let projectId = stepOneData?.projectId;
      // Get stackName from form values (entered on step 2)
      let stackName: string = (values as any).stackName || '';
        const projectCreatePayload = {
          utility_id: stepOneData.utility_id,
          awsRegion: stepOneData.awsRegion,
          country: stepOneData.country,
          stateAlphaCode: stepOneData.stateCode || '',
          projectId: stepOneData.projectId,
          name: stepOneData.name,
          description: stepOneData.description,
          projectImageUrl: stepOneData.projectImageUrl,
          languages: stepOneData.languages,
          timeZone: stepOneData.timeZone,
          environment_id: stepOneData.environment_id,
          projectType: stepOneData.projectType,
          unique_groups: configGroupSelection,
          stackName: stackName
        } as any;
        try {
          const createResp = await PilotApis.createPilot(rc, projectCreatePayload);
          if (createResp.error) {
            const errorMessage = ErrorHandler.handleProjectError(createResp.error, 'create');
            setError({ message: errorMessage, code: 'PROJECT_CREATE_FAILED' });
            return;
          }
          toast.success('Project created successfully');
          // ensure local projectId and stackName captured from response
          projectId = projectCreatePayload.projectId;
          try {
            const createdProject = (createResp as any)?.data?.project || (createResp as any)?.data?.data?.project || (createResp as any)?.data;
            stackName = createdProject?.stackName || '';
          } catch {}
          sessionStorage.setItem(FORM_PART_ONE_KEY, JSON.stringify({ ...stepOneData, projectId, stackName }));
        } catch (e) {
          const errorMessage = ErrorHandler.handleProjectError(e, 'create');
          setError({ message: errorMessage, code: 'PROJECT_CREATE_FAILED' });
          return;
        }

      // 2) Conditionally create environment (only if user selected new environment in step 1)
      try {
          const formValues = getValues() as any;
          const toolApiUrl = process.env.REACT_APP_TOOL_API_URL;
          const environmentType = toolApiUrl?.includes('deto-prod-api.bidgely.com') ? 'uat' : 'dev';
          const environmentData = {
            utilityName: stepOneData.utility?.name || '',
            environmentName: stepOneData.environment_name,
            stateAlphaCode: stepOneData.stateCode?.toLowerCase() || '',
            customerType: formValues?.launchpad_ingestion_configs?.default_user_segment=="RESIDENTIAL" ? "RESI" : "SMB",
            isExistingPilot: !!stepOneData.environment_id,
            stackName: stackName,
            type: environmentType,


            ...(stepOneData.environment_id && { environment_id: stepOneData.environment_id }),
            ...(projectId && { projectId: projectId })
          };
          // /v1/environments is not project-based anymore
          const envResp = await PilotApis.createEnvironment(rc, {
            // required
            name: stepOneData.environment_name,
            ...(stepOneData.utility_id ? { utility_id: stepOneData.utility_id } : { utilityName: stepOneData.utility?.name || '' }),

            // optional (if available)
            ...(stackName ? { stackName } : {}),
            ...(stepOneData.stateCode ? { stateAlphaCode: stepOneData.stateCode } : {}),
            ...environmentData
          });
          if (envResp.error) {
            const errorMessage = ErrorHandler.handleEnvironmentError(envResp.error, 'create');
            setError({ message: errorMessage, code: 'ENV_CREATION_ERROR' });
            return;
          }
      } catch (e) {
        const errorMessage = ErrorHandler.handleEnvironmentError(e, 'create');
        setError({ message: errorMessage, code: 'ENV_CREATION_ERROR' });
        return;
      }

      // 3) Save all configs for the project (step 1 METADATA + step 2 SYSTEM/FEATURE)
      // Use updatedConfigRegistryData if available, otherwise fall back to original configRegistryData
      const configDataToUse = updatedConfigRegistryData.length > 0 ? updatedConfigRegistryData : filterRegistryByComponents(configRegistryData);
      
      // Filter configs based on group selection before saving
      const filteredConfigData = getConfigsToSave(configDataToUse, configGroupSelection);
      
      try {
        if (filteredConfigData.length > 0 && projectId) {
          const configsToSave: SaveProjectConfigData[] = [];
          const duplicateConfigsToSave: SaveProjectConfigData[] = [];
          const addedDuplicates = new Set<string>();
          
          // Load any configs captured on step 1 from session storage
          let stepOneConfigsValues: any = {};
          try {
            const raw = sessionStorage.getItem(FORM_PART_ONE_CONFIGS_KEY);
            if (raw) stepOneConfigsValues = JSON.parse(raw);
          } catch (e) {
            stepOneConfigsValues = {};
          }

          // Step 2 configs: SYSTEM/FEATURE
          const step2ConfigData = filterFirstStepConfigs(filteredConfigData);
          step2ConfigData.forEach((configType: ConfigRegistryType) => {
            configType.configs.forEach((config: any) => {
              if (config.utilityConfigurationRequired && (config.configClassification === 'SYSTEM' || config.configClassification === 'FEATURE')) {
                const typeObj = values[configType.configType];
                const hasFormProp = typeObj && Object.prototype.hasOwnProperty.call(typeObj, config.configKey);
                const uiRawValue = hasFormProp ? typeObj[config.configKey] : undefined;
                const isFromFormValue = hasFormProp && uiRawValue !== undefined && uiRawValue !== null;
                const currentValue = isFromFormValue ? uiRawValue : (config.defaultValue ?? '');
                let configValue: string;
                if (typeof currentValue === 'object' && !Array.isArray(currentValue)) {
                  configValue = JSON.stringify(currentValue);
                }else if(Array.isArray(currentValue)){
                  configValue = JSON.stringify(currentValue);
                } else {
                  configValue = String(currentValue);
                }
                // IMPORTANT: pass the stringified value to the processor to avoid [object Object]
                configValue = processConfigValue(configValue, config.configValueMetadata);
                configsToSave.push({
                  project_id: projectId,
                  config_key: config.configKey,
                  config_type: configType.configType,
                  config_value: configValue,
                  config_level: 'pilot',
                  config_documentation: config.configDocumentation || '',
                  config_regex: config.configRegex || '.*',
                  config_data_type: config.configDataType || 'TEXT',  
                  display_name: config.displayName,
                  features: (config.component || 'ingestion'),
                  editable: config.utilityConfigurationRequired !== false,
                  is_required: config.utilityConfigurationRequired,
                });

                // Propagate to duplicates only when source value is from UI and non-empty
                if (isFromFormValue && String(configValue).length > 0) {
                  filteredConfigData.forEach((originalConfigType: any) => {
                    originalConfigType.configs.forEach((originalConfig: any) => {
                      if (!isDuplicateConfig(originalConfig)) return;
                      const duplicates = originalConfig.duplicateConfig;
                      let isDuplicateOfCurrent = false;

                      if (Array.isArray(duplicates) && duplicates.length > 0) {
                        isDuplicateOfCurrent = duplicates.some((dup: any) => {
                          if (dup && typeof dup === 'object') {
                            return dup.configKey === config.configKey;
                          }
                          return dup === config.configKey;
                        });
                      } else if (typeof duplicates === 'string') {
                        isDuplicateOfCurrent = duplicates === config.configKey;
                      }

                      if (isDuplicateOfCurrent) {
                        const key = `${originalConfigType.configType}|${originalConfig.configKey}`;
                        if (addedDuplicates.has(key)) return;
                        addedDuplicates.add(key);
                        duplicateConfigsToSave.push({
                          project_id: projectId,
                          config_key: originalConfig.configKey,
                          config_type: originalConfigType.configType,
                          config_value: configValue,
                          config_level: 'pilot',
                          config_documentation: originalConfig.configDocumentation || '',
                          config_regex: originalConfig.configRegex || '.*',
                          config_data_type: originalConfig.configDataType || 'TEXT',
                          display_name: originalConfig.displayName,
                          features: (originalConfig.component || 'ingestion'),
                          editable: originalConfig.utilityConfigurationRequired !== false,
                          is_required: originalConfig.utilityConfigurationRequired,
                        });
                      }
                    });
                  });
                }
              }
            });
          });
          
          // Step 1 configs: METADATA - use updated config values if available
          filteredConfigData.forEach((configType: any) => {
            configType.configs.forEach((config: any) => {
              if (config.utilityConfigurationRequired && config.configClassification === 'METADATA') {
                // Check if we have an updated config_value from FIRST_STEP_CONFIG_MAPPING
                let isFromStepOneFormValue = false;
                let currentValue: any;
                if (config.config_value !== undefined) {
                  // Use the updated value from FIRST_STEP_CONFIG_MAPPING
                  currentValue = config.config_value;
                  isFromStepOneFormValue = true;
                } else {
                  // Fall back to session storage values
                  const typeObj = stepOneConfigsValues[configType.configType];
                  currentValue = typeObj ? typeObj[config.configKey] : undefined;
                  if (currentValue === undefined) return; // Skip if no value found
                  
                  if (typeof currentValue === 'object' && !Array.isArray(currentValue)) {
                    currentValue = JSON.stringify(currentValue);
                  }else if(Array.isArray(currentValue)){
                    currentValue = JSON.stringify(currentValue);
                  } else {
                    currentValue = String(currentValue);
                  }
                  isFromStepOneFormValue = true;
                }

                const configValue = processConfigValue(currentValue, config.configValueMetadata);
                
                configsToSave.push({
                  project_id: projectId,
                  config_key: config.configKey,
                  config_type: configType.configType,
                  config_value: configValue,
                  config_level: 'pilot',
                  config_documentation: config.configDocumentation || '',
                  config_regex: config.configRegex || '.*',
                  config_data_type: config.configDataType || 'TEXT',
                  display_name: config.displayName,
                  features: (config.component || 'ingestion'),
                  editable: config.utilityConfigurationRequired !== false,
                  is_required: config.utilityConfigurationRequired,
                });

                // Propagate to duplicates when step-one value exists and is non-empty
                if (isFromStepOneFormValue && String(configValue).length > 0) {
                  filteredConfigData.forEach((originalConfigType: any) => {
                    originalConfigType.configs.forEach((originalConfig: any) => {
                      if (!isDuplicateConfig(originalConfig)) return;
                      const duplicates = originalConfig.duplicateConfig;
                      let isDuplicateOfCurrent = false;

                      if (Array.isArray(duplicates) && duplicates.length > 0) {
                        isDuplicateOfCurrent = duplicates.some((dup: any) => {
                          if (dup && typeof dup === 'object') {
                            return dup.configKey === config.configKey;
                          }
                          return dup === config.configKey;
                        });
                      } else if (typeof duplicates === 'string') {
                        isDuplicateOfCurrent = duplicates === config.configKey;
                      }

                      if (isDuplicateOfCurrent) {
                        const key = `${originalConfigType.configType}|${originalConfig.configKey}`;
                        if (addedDuplicates.has(key)) return;
                        addedDuplicates.add(key);
                        duplicateConfigsToSave.push({
                          project_id: projectId,
                          config_key: originalConfig.configKey,
                          config_type: originalConfigType.configType,
                          config_value: configValue,
                          config_level: 'pilot',
                          config_documentation: originalConfig.configDocumentation || '',
                          config_regex: originalConfig.configRegex || '.*',
                          config_data_type: originalConfig.configDataType || 'TEXT',
                          display_name: originalConfig.displayName,
                          features: (originalConfig.component || 'ingestion'),
                          editable: originalConfig.utilityConfigurationRequired !== false,
                          is_required: originalConfig.utilityConfigurationRequired,
                        });
                      }
                    });
                  });
                }
              }
            });
          });
          const allToSave = [...configsToSave, ...duplicateConfigsToSave];
          if (allToSave.length > 0) {
            const saveResp = await PilotApis.saveProjectConfigs(rc, allToSave);
            if (!saveResp.success) {
              const errorMessage = ErrorHandler.handleConfigError(saveResp.error, 'save');
              toast.warning(errorMessage || 'Some configurations failed to save. You can update them later.');
            } else {
              toast.success(`Successfully saved ${allToSave.length} configuration(s).`);
            }
          }
        }
      } catch (e) {
        const errorMessage = ErrorHandler.handleConfigError(e, 'save');
        toast.warning(errorMessage || 'Configurations failed to save.');
      }

      // 4) Save default configs (with variable substitution) directly to DB
      try {
        if (filteredConfigData.length > 0 && projectId) {
          const allowedFileTypes = ['ALL', 'USER_META', 'METER', 'CONSUMPTION', 'BC_SCHEDULE', 'BILLING'];
          const defaultConfigs = (filteredConfigData as any)
            .map((configType: any) => ({
              ...configType,
              configs: configType.configs.filter((config: any) => {
                const hasDefaultValue = !!config.defaultValue && (typeof config.defaultValue === 'string' ? config.defaultValue.trim() !== '' : true);
                // Use shared duplicate checker utility
                const isNotDuplicate = !isDuplicateConfig(config);
                const hasValidFileType = !config.fileTypes || config.fileTypes.some((fileType: string) => allowedFileTypes.includes(fileType));
                return hasDefaultValue && isNotDuplicate && hasValidFileType;
              })
            }))
            .filter((configType: any) => configType.configs.length > 0);

          const defaultConfigsToSave: SaveProjectConfigData[] = [] as any;

          defaultConfigs.forEach((configType: any) => {
            configType.configs.forEach((config: any) => {
              if (config.defaultValue && (typeof config.defaultValue !== 'string' || config.defaultValue.trim() !== '')) {
                let configValue
                if (config.configDataType === 'BOOLEAN') {
                  configValue = config.defaultValue === 'true' || config.defaultValue === 'TRUE' ? 'true' : 'false';
                } else {
                  configValue = String(config.defaultValue);
                  if (configValue.includes('${env}')) {
                    // Check if REACT_APP_TOOL_API_URL is dev environment
                    const toolApiUrl = process.env.REACT_APP_TOOL_API_URL || '';
                    const env = toolApiUrl.includes('deto-prod-api.bidgely.com') ? 'uat' : 'dev';
                    configValue = configValue.replace(/\$\{env\}/g, env);
                  }
                  if (configValue.includes('${utilityId}')) {
                    configValue = configValue.replace(/\$\{utilityId\}/g, stepOneData.projectId.toString());
                  }
                  if (configValue.includes('${queue-suffix}')) {
                    if (stackName) {
                      configValue = configValue.replace(/\$\{queue-suffix\}/g, stackName);
                    }
                  }
                }
                configValue = processConfigValue(configValue, config.configValueMetadata);
                // Apply variable substitutions
                
                defaultConfigsToSave.push({
                  project_id: projectId,
                  config_key: config.configKey,
                  config_type: configType.configType,
                  config_value: configValue,
                  config_documentation: config.configDocumentation || '',
                  config_regex: config.configRegex || '.*',
                  config_data_type: config.configDataType || 'TEXT',
                  config_level: 'pilot',
                  display_name: config.displayName,
                  features: (config.component || ''),
                  editable: config.utilityConfigurationRequired !== false,
                  is_required: config.utilityConfigurationRequired,
                });
              }
            });
          });

          if (defaultConfigsToSave.length > 0) {
            const saveResp = await PilotApis.saveProjectConfigs(rc, defaultConfigsToSave);
            if (!saveResp.success) {
              const errorMessage = ErrorHandler.handleConfigError(saveResp.error, 'save');
              toast.warning(errorMessage || 'Some default configurations failed to save.');
            }
          }
        }
      } catch (e) {
        const errorMessage = ErrorHandler.handleConfigError(e, 'save');
        toast.warning(errorMessage || 'Default configurations failed to save.');
      }

      // Cleanup and navigate
        sessionStorage.removeItem(FORM_PART_ONE_KEY);
        sessionStorage.removeItem(FORM_PART_TWO_KEY);
        sessionStorage.removeItem(ENVIRONMENT_CREATION_DATA_KEY);
      sessionStorage.removeItem(FORM_PART_ONE_CONFIGS_KEY);
        dispatch(clearConfigRegistryData());
        dispatch(clearFormData());
        rc.uiRouter.navigate(ROUTE_TREE.PROJECT.LIST);
    } catch (err) {
      const errorMessage = ErrorHandler.handleProjectError(err, 'create');
      setError({ message: errorMessage, code: 'GENERAL_ERROR' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToCreate = () => {
    // Preserve projectId when going back
    if (stepOneData && stepOneData.projectId) {
      console.log('Preserving projectId when going back:', stepOneData.projectId);
      
      // Ensure the projectId is preserved in session storage for create mode
      const createData = sessionStorage.getItem(FORM_PART_ONE_KEY);
      if (createData) {
        const parsedData = JSON.parse(createData);
        const updatedData = {
          ...parsedData,
          projectId: stepOneData.projectId,
        };
        sessionStorage.setItem(FORM_PART_ONE_KEY, JSON.stringify(updatedData));
        console.log('Updated create data with projectId:', updatedData);
      }
    }
    
    rc.uiRouter.goBack();
  };



  if (!stepOneData || (configRegistryData.length === 0 && isLoadingRegistry)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        {configRegistryData.length === 0 && isLoadingRegistry && (
          <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading configuration registry...
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      position: 'relative', // Add this for overlay positioning
    }}>
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 3,
      }} ref={scrollContainerRef}>
        <Box sx={{ p:4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h4" sx={{ 
                  fontSize: '1.8rem', 
                  display: 'flex', 
                  alignItems: 'center'
                }}>
                  <Package style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={34} />
                  Create New Project
            </Typography>
            {!isEditMode && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Step 2 of 2
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ maxWidth: '800px', mx: 'auto', pb: 4 }}>
          <Card sx={{ bgcolor: 'background.paper', boxShadow: 4, borderRadius: 2 }}>
            <Box sx={{ p: 3, }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h4" sx={{ 
                  fontSize: '1.5rem', 
                  display: 'flex', 
                  alignItems: 'center'
                }}>
                  <DatabaseZap style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={30} />
                  Ingestion Configs
                </Typography>
                {/* <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Step 2 of 2
                </Typography> */}
              </Box>
              <Typography variant="body1" sx={{ fontSize: '1rem', color: 'text.secondary',textAlign:"left",mt:1 }}>
                Configure the project settings and data source information.
              </Typography>
            </Box>
            <CardContent sx={{ p: 4,pt: 0 }}>
              {/* Header */}
              {/* <Box sx={{ mb: 4 }}>
                <Typography variant="bglTitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FileSignatureIcon sx={{ color: 'primary.main' }} /> Project Details (Step 2 of 2)
                </Typography>
                <Typography variant="bglBody2" color="text.secondary">
                  Provide the project and data source details for the new project.
                </Typography>
              </Box> */}

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error.message}
                </Alert>
              )}

              {isLoadingRegistry && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    Loading configuration registry...
                  </Box>
                </Alert>
              )}

              {registryError && !isLoadingRegistry && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      Failed to load configuration registry: {registryError}
                    </Typography>
                    <BglPrimaryOutlinedButton
                      size="small"
                      onClick={reloadRegistryData}
                      disabled={isLoadingRegistry}
                    >
                      {isLoadingRegistry ? (
                        <>
                          <CircularProgress size={14} sx={{ mr: 1 }} />
                          Loading...
                        </>
                      ) : (
                        'Retry'
                      )}
                    </BglPrimaryOutlinedButton>
                  </Box>
                </Alert>
              )}

              

              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Dynamic Configuration Form */}
                  {configRegistryData.length > 0 ? (
                    <DynamicConfigForm
                      configRegistryData={filterFirstStepConfigs(filterRegistryByComponents(configRegistryData))}
                      control={control}
                      errors={errors}
                      setValue={setValue}
                      getValues={getValues}
                      projectId={stepOneData?.projectId}
                      onGroupSelectionChange={handleConfigGroupSelectionChange}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading configuration form...
                      </Typography>
                    </Box>
                  )}

                  {/* Stack Name (Queue Suffix) Field */}
                  <Box sx={{ mt: 2 }}>
                    <Controller
                      name="stackName"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!(errors as any).stackName}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                              Stack Name (Queue Suffix)
                              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                            </Typography>
                            <Tooltip title="A unique suffix for naming queues and S3 buckets. It should closely match or resemble the utility name." arrow placement="top">
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <TextField
                            {...field}
                            value={field.value || ''}
                            placeholder="Stack Name (Queue Suffix)"
                            size="small"
                            error={!!(errors as any).stackName || !!stackNameError}
                            disabled={!!stepOneData?.environment_id}
                            onChange={(e) => {
                              const value = e.target.value.toLowerCase();
                              field.onChange(value);
                              // Clear error when user starts typing
                              if (stackNameError) {
                                setStackNameError(null);
                              }
                            }}
                            onBlur={(e) => {
                              // Validate on blur - don't modify the value
                              const value = e.target.value;
                              if (value && !STACK_NAME_REGEX.test(value)) {
                                setStackNameError('Invalid format. Only lowercase letters and numbers allowed (e.g., utilityname).');
                              } else {
                                setStackNameError(null);
                              }
                            }}
                            inputProps={{
                              pattern: '^[a-z0-9]+$',
                              title: 'Only lowercase letters and numbers are allowed (e.g., utilityname)'
                            }}
                            sx={{
                              '& .Mui-disabled': {
                                bgcolor: 'action.disabledBackground',
                              }
                            }}
                          />
                          <FormHelperText error={!!(errors as any).stackName || !!stackNameError}>
                            {stackNameError || (errors as any).stackName?.message || (stepOneData?.environment_id ? 'Prefilled from selected environment' : '')}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />

                    {/* Naming Convention Info Box */}
                    {stackNameValue && stackNameValue.length > 0 && !stepOneData?.environment_id && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          border: '2px dashed',
                          borderColor: 'primary.main',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 22, mt: 0.25 }} />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                              Naming Convention Info
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                              The Stack Name impacts resource naming:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2, color: 'text.secondary' }}>
                              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                <strong>SQS Queue:</strong> <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: 4 }}>RES-QLD-S3-{'{'}<span style={{ color: '#00bfa5' }}>FileType</span>{'}'}-uat-<span style={{ color: '#00bfa5', fontWeight: 600 }}>{stackNameValue || 'a'}</span></code>
                              </Typography>
                              <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                <strong>External S3 Bucket:</strong> <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: 4 }}>bidgely-<span style={{ color: '#00bfa5', fontWeight: 600 }}>{stackNameValue || 'a'}</span>-uat-external</code>
                              </Typography>
                              <Typography component="li" variant="body2">
                                <strong>Internal S3 Bucket:</strong> <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: 4 }}>bidgely-<span style={{ color: '#00bfa5', fontWeight: 600 }}>{stackNameValue || 'a'}</span>-RES-QLD-uat</code>
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: 2, 
                    mt: 2,
                    pt: 3,
                    p: 3,
                    position: 'sticky',
                    bottom: 0,
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    boxShadow: '0px -4px 8px rgba(0, 0, 0, 0.1)',
                  }}>
                    <BglSecondaryButton
                      onClick={handleBackToCreate}
                      disabled={isLoading || isLoadingRegistry}
                      startIcon={<ArrowBackIcon />}
                    >
                      Back to Core Attributes
                    </BglSecondaryButton>
                    <BglPrimaryButton
                      type="submit"
                      disabled={isLoading || isLoadingRegistry || configRegistryData.length === 0}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Submitting...
                        </>
                      ) : isLoadingRegistry || configRegistryData.length === 0 ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          Submit Project Request
                          <Send style={{ marginLeft: 4 }} size={18} />
                        </>
                      )
                    }
                    </BglPrimaryButton>
                  </Box>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      {/* Overlay Loader */}
      <BglOverlayLoader
        open={isLoading}
        message="Creating project..."
        size={50}
      />
    </Box>
  );
};

// Component for viewing project details (route: /utility/:id)
export const UtilityHomePage = () => {
  const { rc } = useGlobalConfig();
  
  return (
    <Box sx={{ 
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
    }}>
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 3,
      }}>
        <Box sx={{ maxWidth: '800px', mx: 'auto', pb: 4 }}>
          <Card sx={{ bgcolor: 'background.paper', boxShadow: 4, borderRadius: 2 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="bglTitle2" sx={{ mb: 2 }}>
                Project Details
              </Typography>
              <Typography variant="bglBody1" color="text.secondary">
                Project details view - TODO: Implement project details display
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}; 